import { useToast } from '@pelckmans/elementis/components/toast';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LocalStorageKeys from '../../constants/LocalStorageKeys';
import { TOAST_ID } from '../../constants/ToastIds';
import IdentityContext from '../../contexts/IdentityContext';
import { useRoutes } from '../../hooks/useRoutes';
import { syncAccount } from '../../modules/my-account/api/MyAccountApi';
import LocalStorage from '../../utils/LocalStorage';

type ConnectCallbackError = 'user_already_connected' | 'duplicate_platform' | 'user_cancel' | 'account_incomplete';

export default function ConnectCallback() {
  const { t } = useTranslation();
  const { buildAbsolutePath } = useRoutes();
  const { onAccessTokenReceived } = useContext(IdentityContext);
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    const runConnectCallback = async () => {
      if (query.has('error')) {
        const callbackError = query.get('error');

        switch (callbackError as ConnectCallbackError) {
          case 'user_already_connected':
            addToast({
              id: TOAST_ID.CONNECT_CALLBACK_ERROR,
              variant: 'danger',
              title: t('TOAST.DANGER.TITLE'),

              children: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONNECT_CALLBACK.ALREADY_CONNECTED'),
            });
            break;
          case 'duplicate_platform':
            addToast({
              id: TOAST_ID.CONNECT_CALLBACK_ERROR,
              variant: 'danger',
              title: t('TOAST.DANGER.TITLE'),

              children: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONNECT_CALLBACK.DUPLICATE_PLATFORM'),
            });
            break;
          case 'user_cancel':
            break;
          default:
            addToast({
              id: TOAST_ID.CONNECT_CALLBACK_ERROR,
              variant: 'danger',
              title: t('TOAST.DANGER.TITLE'),

              children: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONNECT_CALLBACK.GENERAL_ERROR'),
            });
        }
      } else {
        const { access_token, refresh_token } = await syncAccount();

        LocalStorage.set(LocalStorageKeys.p_access_token, JSON.stringify(access_token));
        LocalStorage.set(LocalStorageKeys.p_refresh_token, JSON.stringify(refresh_token));

        onAccessTokenReceived(access_token);
        addToast({
          id: TOAST_ID.CONNECT_CALLBACK_SUCCESS,
          variant: 'success',
          title: t('TOAST.SUCCESS.TITLE'),

          children: t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.CONNECT_CALLBACK.SUCCESS'),
        });
      }

      navigate(buildAbsolutePath(['MY_PROFILE', 'MY_PROFILE.PERSONAL_DETAILS']));
    };

    runConnectCallback();
  }, [navigate, onAccessTokenReceived, query, buildAbsolutePath, addToast, t]);

  return null;
}
